const FALLBACK_I18N = {
  required_field: 'Dit veld is verplicht',
  details_heading: 'Zendinggegevens',
  shipment_label: 'Zending __number__',
  barcode_label: 'Barcode:',
  track_trace: 'Track & Trace',
  no_results: 'Geen zendingen gevonden voor deze bestelling.',
  loading: 'Laden...',
  error_not_found: 'Bestelling niet gevonden. Controleer je bestelnummer en postcode.',
  error_invalid: 'Ongeldige invoer. Controleer je bestelnummer en postcode.',
  error_rate_limit: 'Te veel aanvragen. Probeer het later opnieuw.',
  error_generic: 'Er is een fout opgetreden bij het ophalen van de zendinggegevens.',
  error_network: 'Netwerkfout: __message__. Controleer je verbinding en probeer het opnieuw.',
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const initSection = (section) => {
  if (section.dataset.fwTrackingReady === 'true') return;
  section.dataset.fwTrackingReady = 'true';

  const form = section.querySelector('[data-fw-tracking-form]');
  const feedback = section.querySelector('[data-fw-tracking-feedback]');
  const submit = section.querySelector('[data-fw-tracking-submit]');
  const submitLabel = section.querySelector('[data-fw-tracking-submit-label]');
  const orderInput = form?.querySelector('[name="order_id"]');
  const zipcodeInput = form?.querySelector('[name="zipcode"]');

  if (!form || !feedback || !orderInput || !zipcodeInput) return;

  const apiBaseUrl = (section.dataset.apiBaseUrl || 'https://hub.fitwinkel.nl').replace(/\/$/, '');
  const defaultSubmitLabel = submitLabel?.textContent || '';

  let i18n = { ...FALLBACK_I18N };
  const i18nScript = section.querySelector('[data-fw-tracking-i18n]')
    || (document.documentElement.lang.toLowerCase().startsWith('de')
      ? section.parentElement?.querySelector('[data-fw-tracking-i18n]')
      : null);
  if (i18nScript) {
    try {
      i18n = { ...FALLBACK_I18N, ...JSON.parse(i18nScript.textContent) };
    } catch {
      i18n = { ...FALLBACK_I18N };
    }
  }

  const t = (key, replacements = {}) => {
    let value = i18n[key] || FALLBACK_I18N[key] || '';
    for (const [token, replacement] of Object.entries(replacements)) {
      value = value.replaceAll(`__${token}__`, replacement);
    }
    return value;
  };

  // Prefill from query params or path (e.g. /pages/tracking?order=pl123-B&zipcode=1234AB)
  const params = new URLSearchParams(window.location.search);
  const orderFromUrl = params.get('order') || params.get('order_id');
  const zipcodeFromUrl = params.get('zipcode');
  if (orderFromUrl) orderInput.value = orderFromUrl.trim();
  if (zipcodeFromUrl) zipcodeInput.value = zipcodeFromUrl.trim();

  const setFieldError = (input, message) => {
    const field = input.closest('.fw-tracking__field');
    if (!field) return;
    field.querySelector('.fw-tracking__error')?.remove();
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      const error = document.createElement('span');
      error.className = 'fw-tracking__error';
      error.textContent = message;
      field.appendChild(error);
    } else {
      input.removeAttribute('aria-invalid');
    }
  };

  const validate = () => {
    let valid = true;
    [orderInput, zipcodeInput].forEach((input) => {
      if (!input.value.trim()) {
        setFieldError(input, t('required_field'));
        valid = false;
      } else {
        setFieldError(input, '');
      }
    });
    return valid;
  };

  const renderAlert = (type, message) => {
    feedback.innerHTML = `<div class="fw-tracking__alert fw-tracking__alert--${type}">${escapeHtml(message)}</div>`;
  };

  const renderShipments = (shipments) => {
    const items = shipments
      .map((shipment, index) => {
        const trackButton = shipment.track_trace_url
          ? `<a class="button button--small" href="${escapeHtml(shipment.track_trace_url)}" target="_blank" rel="noopener">${escapeHtml(t('track_trace'))}</a>`
          : '';
        return `
          <div class="fw-tracking__shipment">
            <div class="fw-tracking__shipment-title">${escapeHtml(t('shipment_label', { number: index + 1 }))}</div>
            <div class="fw-tracking__barcode-row">
              <strong>${escapeHtml(t('barcode_label'))}</strong>
              <span class="fw-tracking__barcode">${escapeHtml(shipment.barcode || '')}</span>
            </div>
            ${trackButton}
          </div>`;
      })
      .join('');

    feedback.innerHTML = `
      <h2 class="fw-tracking__results-heading">${escapeHtml(t('details_heading'))}</h2>
      <div class="fw-tracking__shipments">${items}</div>`;
  };

  const setLoading = (loading) => {
    if (loading) {
      submit.setAttribute('aria-busy', 'true');
      submit.disabled = true;
      if (submitLabel) submitLabel.textContent = t('loading');
    } else {
      submit.removeAttribute('aria-busy');
      submit.disabled = false;
      if (submitLabel) submitLabel.textContent = defaultSubmitLabel;
    }
  };

  const trackShipment = async () => {
    if (!validate()) return;

    setLoading(true);
    feedback.innerHTML = '';

    const orderId = orderInput.value.trim();
    const query = new URLSearchParams({ zipcode: zipcodeInput.value.trim() });
    const url = `${apiBaseUrl}/api/v1/public/shipments/${encodeURIComponent(orderId)}?${query}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        if (response.status === 404) {
          renderAlert('error', t('error_not_found'));
        } else if (response.status === 422) {
          renderAlert('error', data.detail?.[0]?.msg || t('error_invalid'));
        } else if (response.status === 429) {
          renderAlert('error', t('error_rate_limit'));
        } else {
          renderAlert('error', typeof data.detail === 'string' ? data.detail : t('error_generic'));
        }
        return;
      }

      const shipments = Array.isArray(data.shipments) ? data.shipments : [];
      if (shipments.length === 0) {
        renderAlert('info', t('no_results'));
      } else {
        renderShipments(shipments);
      }
    } catch (err) {
      renderAlert('error', t('error_network', { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    trackShipment();
  });

  [orderInput, zipcodeInput].forEach((input) => {
    input.addEventListener('input', () => setFieldError(input, ''));
  });
};

const init = () => {
  document.querySelectorAll('[data-fw-tracking]').forEach(initSection);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Re-init when the section is reloaded in the theme editor
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-fw-tracking]').forEach(initSection);
});
